/**
 * PANNELLO MAPPA EIDOS — sostituisce il campo di testo "Indirizzo" del configuratore.
 *
 * Cosa fa:
 *  - una casella con i SUGGERIMENTI dell'indirizzo mentre si scrive (Mapbox Geocoder);
 *  - una MAPPA trascinabile col dito/mouse: il segnaposto sta fermo sull'indirizzo, si sposta la mappa;
 *  - sulla mappa disegna il CONTORNO del gioiello scelto (modello, forma, misura, zoom): la zona dentro il
 *    contorno e' quella che finisce sul gioiello; se il segnaposto esce dal contorno lo dice (25 set 2026);
 *  - quando si sceglie un suggerimento o si lascia la mappa, manda al modello le coordinate
 *    "lat,lon" del centro nel parametro "Indirizzo" (il servizio strade Eidos le accetta cosi').
 *
 * I contorni stanno in public/forme.json (millimetri, esportati da Rhino con la libreria del motore).
 * Tutti gli altri parametri di testo (es. la dedica) continuano a usare il componente standard.
 * Questo file sta FUORI da src/shared: gli aggiornamenti di ShapeDiver non lo toccano.
 */
import ParameterStringComponent from "@AppBuilderLib/entities/parameter/ui/ParameterStringComponent";
import {useParameterComponentCommons} from "@AppBuilderLib/entities/parameter/model/useParameterComponentCommons";
import {useShapeDiverStoreParameters} from "@AppBuilderLib/entities/parameter/model/useShapeDiverStoreParameters";
import {IShapeDiverParameter} from "@AppBuilderLib/entities/parameter/config/parameter";
import {
	PropsParameterComponent,
	PropsParameterWrapper,
} from "@AppBuilderLib/entities/parameter/config/propsParameter";
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import "@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css";
import {Box, Button, Text} from "@mantine/core";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import {useEffect, useRef, useState} from "react";
import {useShallow} from "zustand/react/shallow";

// Chiave PUBBLICA di Mapbox (inizia con "pk."): e' fatta per stare nel codice visibile. La stessa del servizio strade.
const MAPBOX_PK =
	"pk.eyJ1IjoiZm9mZm8iLCJhIjoiY211MmltazU5MDA0ZTJ6cXY2ZWNvcWV2MyJ9.7N9Y4dTtGuunQgCoJCzr7w";

/** Nome del parametro del modello che questo pannello sostituisce. */
const NOME_PARAMETRO_INDIRIZZO = "Indirizzo";

/** Centro iniziale (e ritorno del totem): Via Cimino 3, L'Aquila (Davide, 29 set 2026; prima Piazza del Duomo, Milano).
 *  Coordinate [longitudine, latitudine] dal geocoder Mapbox. */
const CENTRO_PREDEFINITO: [number, number] = [13.3992, 42.34858];
const INDIRIZZO_PREDEFINITO = "Via Cimino 3, L'Aquila";
/** vero dopo il primo montaggio del pannello mappa nella pagina */
let primoAvvioFatto = false;

/** id del parametro "Indirizzo" (noto quando il pannello mappa e' montato) */
let idParametroIndirizzo: string | null = null;
/**
 * (24 ott 2026, video di Davide dall'iPhone) ULTIMO INDIRIZZO SCELTO: sul telefono le schede di Mantine 9 tengono la
 * scheda nascosta in <Activity>, e React, cambiando scheda, distrugge e ricrea l'effetto della mappa (mappa e casella
 * nuove). Senza questo ricordo la casella tornava a "Via Cimino 3" (o restava vuota) mentre il segnaposto era altrove.
 */
let ultimaScelta: {testo: string; segnaposto: [number, number]} | null = null;
/**
 * Valore dell'indirizzo per il RITORNO ALLO STATO DI PARTENZA del totem (28 set sera): le coordinate del centro
 * predefinito. Se si rimettesse il valore iniziale del modello (un indirizzo scritto a parole) il modello
 * risponderebbe con l'elenco "Indirizzi trovati: scegli il numero..." che nel fork non ha pulsanti.
 */
export function indirizzoDiPartenza(): {id: string; testo: string} | null {
	return idParametroIndirizzo ? {id: idParametroIndirizzo, testo: scriviCoord(CENTRO_PREDEFINITO)} : null;
}

const OTTANIO = "#134D5D";
/** sfondo della mappa = crema del sito */
const CREMA_MAPPA = "#F6F4F2";
const ROSSO = "#b0413e";
/** il servizio strade di Eidos (Cloudflare Worker): lo stesso indirizzo che chiama il modello Grasshopper */
const URL_STRADE = "https://eidos-strade.eidos-direzionale.workers.dev/strade";

// ---- Le altre scelte del cliente che servono per disegnare il contorno (nomi mostrati -> valori del motore)
const MODELLI: Record<string, string> = {
	Collana: "collana",
	Bracciale: "bracciale",
	Anello: "anello",
	"Orecchino a lobo": "lobo",
	"Orecchino pendente": "pendente",
};
/** "Zoom mappa": metri di mappa per ogni millimetro di gioiello... diviso 2 (vedi metriPerMm). */
const ZOOM_K: Record<string, number> = {
	"Molto vicino": 8,
	Vicino: 11,
	Medio: 14,
	Ampio: 18,
};
/** Il motore mette zoom_m = k x lato maggiore della zona mappa = metri dal CENTRO al bordo: quindi 1 mm = 2k metri. */
function metriPerMm(k: number): number {
	return 2 * k;
}

type Forme = Record<string, number[][]>;

/** "45.4642,9.19" -> [lon, lat] (l'ordine di Mapbox), oppure null se non sono coordinate. */
function leggiCoordinate(testo: string): [number, number] | null {
	const m = /^\s*(-?\d{1,2}(?:\.\d+)?)\s*[,; ]\s*(-?\d{1,3}(?:\.\d+)?)\s*$/.exec(
		testo || "",
	);
	if (!m) return null;
	const lat = parseFloat(m[1]);
	const lon = parseFloat(m[2]);
	if (Math.abs(lat) > 85 || Math.abs(lon) > 180) return null;
	return [lon, lat];
}

/**
 * Il valore del parametro "Indirizzo" scritto dal pannello:  "centro"  oppure  "centro|segnaposto"
 * (coppie lat,lon). Il centro e' il centro della mappa (= centro del gioiello), il segnaposto e' l'indirizzo scelto.
 */
function leggiValore(testo: string): {
	centro: [number, number] | null;
	segnaposto: [number, number] | null;
} {
	const [a, b] = String(testo || "").split("|");
	return {centro: leggiCoordinate(a), segnaposto: leggiCoordinate(b || "")};
}

function scriviCoord(c: [number, number]): string {
	return `${c[1].toFixed(5)},${c[0].toFixed(5)}`; // lat,lon
}

/** Spostamento in metri (est, nord) dal punto a al punto b, entrambi [lon, lat]. */
function spostamentoMetri(a: [number, number], b: [number, number]): [number, number] {
	const k = Math.PI / 180;
	const R = 6378137;
	const dx = (b[0] - a[0]) * k * R * Math.cos(((a[1] + b[1]) / 2) * k);
	const dy = (b[1] - a[1]) * k * R;
	return [dx, dy];
}

/** Distanza approssimata in metri fra due punti [lon, lat] (basta per capire se sono "lo stesso punto"). */
function distanzaMetri(a: [number, number], b: [number, number]): number {
	const [dx, dy] = spostamentoMetri(a, b);
	return Math.hypot(dx, dy);
}

/** Il punto (x, y) sta dentro il poligono? (metodo del raggio). */
function dentroPoligono(x: number, y: number, poly: number[][]): boolean {
	let dentro = false;
	for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
		const [xi, yi] = poly[i];
		const [xj, yj] = poly[j];
		if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) dentro = !dentro;
	}
	return dentro;
}

/** Il segnaposto disegnato sulla mappa (un cerchietto ottanio). */
function elementoSegnaposto(): HTMLElement {
	const el = document.createElement("div");
	el.style.cssText =
		"width:18px;height:18px;border-radius:50%;border:3px solid " +
		OTTANIO +
		";background:rgba(246,244,242,0.85);box-shadow:0 0 0 2px rgba(255,255,255,0.8);";
	return el;
}

/**
 * Legge la voce scelta in un menu del modello (es. "Modello" -> "Bracciale") e si aggiorna quando cambia.
 * Per i menu ShapeDiver il valore e' l'indice della voce: qui viene tradotto nel nome mostrato.
 */
function useScelta(namespace: string, nome: string): string {
	const {getParameter} = useShapeDiverStoreParameters(
		useShallow((s) => ({getParameter: s.getParameter})),
	);
	const store = getParameter(namespace, nome);
	const leggi = () => {
		if (!store) return "";
		const p = store.getState() as IShapeDiverParameter<string>;
		const v = String(p.state.uiValue ?? "");
		const scelte = (p.definition as {choices?: string[]}).choices;
		if (scelte && scelte.length) {
			const i = parseInt(v, 10);
			return isNaN(i) ? v : (scelte[i] ?? v);
		}
		return v;
	};
	const [valore, setValore] = useState<string>(leggi);
	useEffect(() => {
		if (!store) return;
		setValore(leggi());
		return store.subscribe(() => setValore(leggi()));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [store]);
	return valore;
}

/** La chiave del contorno in forme.json a partire dalle scelte del cliente. */
function chiaveForma(
	modello: string,
	forma: string,
	misura: string,
	formaAnello: string,
): string {
	const tipo = MODELLI[modello] || "collana";
	if (tipo === "anello") return `anello|${(formaAnello || "quadrifoglio").toLowerCase()}|`;
	let f = (forma || "").toLowerCase();
	if (!f || f.startsWith("—") || f.startsWith("-")) f = "cerchio"; // "— scegli —": intanto il cerchio, come il motore
	const m = tipo === "collana" || tipo === "bracciale" ? (misura || "piccola").toLowerCase() : "";
	return `${tipo}|${f}|${m}`;
}

function PannelloMappa(props: PropsParameterComponent) {
	// debounce 0: quando la mappa si ferma il valore parte subito
	const {state, handleChange, disabled, definition} = useParameterComponentCommons<string>(
		props,
		0,
	);
	// le altre scelte del cliente: servono per il contorno del gioiello sulla mappa
	const modello = useScelta(props.namespace, "Modello");
	const forma = useScelta(props.namespace, "Forma");
	const misura = useScelta(props.namespace, "Misura");
	const formaAnello = useScelta(props.namespace, "Forma anello");
	const zoomMappa = useScelta(props.namespace, "Zoom mappa");
	const chiave = chiaveForma(modello, forma, misura, formaAnello);
	const kZoom = ZOOM_K[zoomMappa] ?? 11;

	const contenitoreMappa = useRef<HTMLDivElement>(null);
	const contenitoreCerca = useRef<HTMLDivElement>(null);
	const contornoRef = useRef<SVGPathElement>(null);
	const veloRef = useRef<SVGPathElement>(null);
	const attesaPartenzaRef = useRef(false);
	// valore corrente del parametro, leggibile anche dentro i gestori della mappa
	const uiRef = useRef(state.uiValue);
	uiRef.current = state.uiValue;
	// "any": i tipi di mapbox-gl 3 e quelli del geocoder descrivono la stessa mappa in due modi diversi
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const mappaRef = useRef<any>(null);
	const handleChangeRef = useRef(handleChange);
	handleChangeRef.current = handleChange;
	const ultimoInviato = useRef<string>("");
	// UN SOLO CALCOLO per gesto (28 set sera): centro della mappa e livello di zoom si accodano qui e partono
	// insieme 150 ms dopo l'ultimo evento, con la funzione dell'SDK che manda piu' parametri in un colpo solo
	const indirizzoDaMandare = useRef<string | null>(null);
	const zoomDaMandare = useRef<{id: string; valore: string} | null>(null);
	const timerInvio = useRef<number | undefined>(undefined);
	const idIndirizzo = definition?.id;
	// (5 ott 2026) SERVIZIO STRADE GIU': quel giorno tutti i server OpenStreetMap erano irraggiungibili per ore e
	// ogni indirizzo nuovo dava una piastrina VUOTA senza nessuna spiegazione ("non si visualizza nulla").
	// Ora, prima di mandare un centro nuovo al modello, il pannello chiede le strade al servizio di Eidos:
	//  - se risponde (200): si manda al modello, che trovera' le strade gia' in memoria (piu' veloce);
	//  - se risponde "non disponibile" (503): NIENTE calcolo (niente crediti sprecati, gioiello invariato) e
	//    compare l'avviso con il pulsante "Riprova";
	//  - se la richiesta non parte proprio (rete): si manda lo stesso, come prima.
	const [servizioGiu, setServizioGiu] = useState<boolean>(false);
	// vero mentre si aspetta la risposta del servizio strade (puo' volerci fino a 10 s se i server sono lenti)
	const [controllo, setControllo] = useState<boolean>(false);
	// valori in attesa del controllo strade: un gesto nuovo durante l'attesa li unisce ai suoi (uno solo vince)
	const inAttesa = useRef<{valori: Record<string, unknown>; testo: string | null} | null>(null);
	const generazioneInvio = useRef(0);
	// (6 ott pomeriggio) il controllo strade in corso: un gesto nuovo lo annulla invece di lasciarlo girare
	const controlloRef = useRef<AbortController | null>(null);
	const manda = (valori: Record<string, unknown>, testo: string | null) => {
		const soloIndirizzo = testo !== null && Object.keys(valori).length === 1;
		if (soloIndirizzo) {
			// solo il centro: come prima, attraverso il componente standard
			handleChangeRef.current(testo, 0);
			return;
		}
		try {
			void useShapeDiverStoreParameters.getState().batchParameterValueUpdate({[props.namespace]: valori}, true, true);
		} catch {
			/* ignora */
		}
	};
	const pianifica = () => {
		if (timerInvio.current !== undefined) window.clearTimeout(timerInvio.current);
		timerInvio.current = window.setTimeout(() => {
			timerInvio.current = undefined;
			const valori: Record<string, unknown> = inAttesa.current ? {...inAttesa.current.valori} : {};
			let testo: string | null = inAttesa.current ? inAttesa.current.testo : null;
			if (indirizzoDaMandare.current !== null && idIndirizzo) {
				valori[idIndirizzo] = indirizzoDaMandare.current;
				testo = indirizzoDaMandare.current;
			}
			if (zoomDaMandare.current) valori[zoomDaMandare.current.id] = zoomDaMandare.current.valore;
			indirizzoDaMandare.current = null;
			zoomDaMandare.current = null;
			inAttesa.current = null;
			if (!Object.keys(valori).length) return;
			if (testo === null) {
				manda(valori, null);
				return;
			}
			// centro nuovo: prima il controllo del servizio strade (la stessa richiesta che fara' il modello)
			const gen = ++generazioneInvio.current;
			inAttesa.current = {valori, testo};
			setControllo(true);
			const centro = testo.split("|")[0];
			// (6 ott pomeriggio, revisione) il controllo stava in SERIE prima del calcolo, con 12 s di attesa massima:
			// con il servizio lento il cliente aspettava fino a 15 s senza che nulla girasse. Ora: al massimo 3 s
			// (il controllo serve solo a non sprecare un calcolo quando il servizio e' GIU' e a scaldare la sua
			// memoria), poi si manda comunque; un gesto nuovo annulla la richiesta vecchia.
			controlloRef.current?.abort();
			const ctrl = new AbortController();
			controlloRef.current = ctrl;
			const scadenza = window.setTimeout(() => ctrl.abort(), 3000);
			fetch(`${URL_STRADE}?c=${encodeURIComponent(centro)}&r=1300`, {signal: ctrl.signal})
				.then((r) => (r.status === 503 ? "giu" : "ok"))
				.catch(() => "rete")
				.then((esito) => {
					window.clearTimeout(scadenza);
					if (gen !== generazioneInvio.current) return; // un gesto piu' nuovo ha gia' preso tutto
					inAttesa.current = null;
					setControllo(false);
					if (esito === "giu") {
						setServizioGiu(true);
						ultimoInviato.current = ""; // cosi' "Riprova" (o un altro gesto) rimanda lo stesso centro
						return;
					}
					setServizioGiu(false);
					manda(valori, testo);
				});
		}, 150);
	};
	// "Riprova" dell'avviso: rimanda il centro attuale della mappa (invia e' definito quando la mappa esiste)
	const inviaRef = useRef<() => void>(() => undefined);
	const riprova = () => {
		setServizioGiu(false);
		ultimoInviato.current = "";
		inviaRef.current();
	};
	// l'indirizzo scelto (dove sta il segnaposto), [lon, lat]; null finche' non si sceglie un suggerimento
	const segnapostoRef = useRef<[number, number] | null>(null);
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const markerRef = useRef<any>(null);
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const cercaRef = useRef<any>(null);
	// il totem (ripartiDaCapo) deve sapere quale parametro e' l'indirizzo e quale testo vale "punto di partenza"
	useEffect(() => {
		idParametroIndirizzo = definition?.id ?? null;
	}, [definition?.id]);
	const [trovato, setTrovato] = useState<string>("");
	const [fuori, setFuori] = useState<boolean>(false);
	const [forme, setForme] = useState<Forme | null>(null);
	const disegnaRef = useRef<() => void>(() => undefined);
	const adattaZoomRef = useRef<() => void>(() => undefined);
	const misuraRiquadro = useRef<string>("");
	// stato corrente (forme, chiave, livello di zoom) letto dal gestore "zoomend" senza ricreare la mappa
	const statoZoomRef = useRef<{forme: Forme | null; chiave: string; kZoom: number}>({forme: null, chiave: "", kZoom: 11});
	statoZoomRef.current = {forme, chiave, kZoom};

	// i contorni delle forme (millimetri), esportati da Rhino
	useEffect(() => {
		let vivo = true;
		fetch("forme.json")
			.then((r) => (r.ok ? r.json() : null))
			.then((d) => {
				if (vivo && d) setForme(d as Forme);
			})
			.catch(() => undefined);
		return () => {
			vivo = false;
		};
	}, []);

	// disegna il contorno del gioiello al centro della mappa, nella scala della mappa; controlla il segnaposto
	disegnaRef.current = () => {
		const mappa = mappaRef.current;
		const path = contornoRef.current;
		if (!mappa || !path) return;
		const poly = forme?.[chiave];
		if (!poly || !poly.length) {
			path.setAttribute("d", "");
			veloRef.current?.setAttribute("d", "");
			return;
		}
		const c = mappa.getCenter();
		const cont = mappa.getContainer() as HTMLElement;
		const w = cont.clientWidth;
		const h = cont.clientHeight;
		// pixel per metro: 100 m verso est, misurati sullo schermo
		const dLng = 100 / (111320 * Math.cos((c.lat * Math.PI) / 180));
		const p0 = mappa.project([c.lng, c.lat]);
		const p1 = mappa.project([c.lng + dLng, c.lat]);
		const ppm = Math.abs(p1.x - p0.x) / 100;
		const mpm = metriPerMm(kZoom); // metri per millimetro di gioiello
		const d = poly
			.map(([x, y], i) => `${i ? "L" : "M"}${(w / 2 + x * mpm * ppm).toFixed(1)} ${(h / 2 + y * mpm * ppm).toFixed(1)}`)
			.join(" ");
		path.setAttribute("d", d + " Z");
		// velo: tutto il riquadro MENO il contorno (regola "evenodd": il gioiello e' il buco nel velo)
		veloRef.current?.setAttribute("d", `M0 0 H${w} V${h} H0 Z ${d} Z`);
		// il segnaposto (l'indirizzo) sta dentro il contorno?
		const seg = segnapostoRef.current;
		if (seg) {
			const [dx, dy] = spostamentoMetri([c.lng, c.lat], seg);
			// il contorno e' in mm con la y verso il basso: metri -> mm, nord = y negativa
			const dentro = dentroPoligono(dx / mpm, -dy / mpm, poly);
			setFuori(!dentro);
			path.setAttribute("stroke", dentro ? OTTANIO : ROSSO);
		} else {
			setFuori(false);
			path.setAttribute("stroke", OTTANIO);
		}
	};

	// porta lo zoom della mappa a far stare il contorno in circa 2/3 del riquadro
	const adattaZoom = () => {
		const mappa = mappaRef.current;
		const poly = forme?.[chiave];
		if (!mappa || !poly || !poly.length) return;
		// (24 ott 2026) mai interrompere il dito del cliente: jumpTo/easeTo fermano anche il trascinamento (sul telefono il
		// messaggio "fuori dal gioiello" che compare cambia la misura della mappa e prima il trascinamento si fermava da
		// solo). Si rifa' quando la mappa si ferma.
		if (mappa.isMoving() && !mappa.isEasing()) {
			mappa.once("moveend", () => adattaZoomRef.current());
			return;
		}
		const cont = mappa.getContainer() as HTMLElement;
		const lato = Math.min(cont.clientWidth, cont.clientHeight);
		let ext = 0;
		for (const [x, y] of poly) ext = Math.max(ext, Math.abs(x), Math.abs(y));
		const metri = 2 * ext * metriPerMm(kZoom); // ingombro del contorno in metri
		const c = mappa.getCenter();
		const dLng = 100 / (111320 * Math.cos((c.lat * Math.PI) / 180));
		const p0 = mappa.project([c.lng, c.lat]);
		const p1 = mappa.project([c.lng + dLng, c.lat]);
		const ppm = Math.abs(p1.x - p0.x) / 100;
		const ppmVoluto = (0.66 * lato) / metri;
		if (!(ppm > 0) || !(metri > 0) || !(lato > 80)) return; // mappa non ancora pronta: niente zoom strani
		let z = mappa.getZoom() + Math.log2(ppmVoluto / ppm);
		if (!isFinite(z)) return;
		z = Math.max(9, Math.min(19, z)); // limiti ragionevoli (Mapbox va da 0 a 22)
		// LIMITI DELLO ZOOM (Davide, 3 ott 2026): con le dita o con +/- si puo' andare solo fra il livello piu'
		// vicino ("Molto vicino", k=8) e il piu' ampio ("Ampio", k=18) del modello, con un po' di margine: cosi' le
		// possibilita' restano 4 e non si fanno calcoli inutili. (zoom di Mapbox: +1 = meta' dei metri sullo schermo)
		try {
			const kk = Object.values(ZOOM_K);
			const zMin = z + Math.log2(kZoom / Math.max(...kk)) - 0.3;
			const zMax = z + Math.log2(kZoom / Math.min(...kk)) + 0.3;
			// (5 ott) ordine: Mapbox rifiuta un minimo piu' alto del massimo attuale (e viceversa), quindi si imposta
			// prima quello che non va in conflitto; altrimenti (piastrina molto piu' piccola) la mappa restava bloccata
			const zMinC = Math.max(9, zMin);
			const zMaxC = Math.min(19, zMax);
			if (zMinC > mappa.getMaxZoom()) {
				mappa.setMaxZoom(zMaxC);
				mappa.setMinZoom(zMinC);
			} else {
				mappa.setMinZoom(zMinC);
				mappa.setMaxZoom(zMaxC);
			}
		} catch {
			/* ignora */
		}
		// (24 ott 2026) niente piu' "vola qui" della casella indirizzo (flyTo: false, vedi il gestore "result"): qui prima
		// si impostava lo zoom del volo
		if (Math.abs(z - mappa.getZoom()) < 0.05) return;
		// (5 ott 2026) zoom ANIMATO solo a mappa caricata e ferma. All'apertura il riquadro cambia misura mentre
		// l'animazione e' in corso e Mapbox tiene fermo il punto in pixel del VECCHIO centro: il centro della mappa
		// scivolava di ~600 m e il segnaposto di partenza finiva fuori dal riquadro ("segnaposto fuori dal gioiello"
		// gia' all'apertura, contorno rosso). Il salto secco non ha questo problema.
		if (mappa.loaded() && !mappa.isMoving()) mappa.easeTo({zoom: z, duration: 400});
		else mappa.jumpTo({zoom: z});
	};
	adattaZoomRef.current = adattaZoom;

	useEffect(() => {
		if (!contenitoreMappa.current || !contenitoreCerca.current) return;
		mapboxgl.accessToken = MAPBOX_PK;
		// la sezione a fisarmonica puo' smontare e rimontare il pannello: si riparte sempre da contenitori vuoti
		contenitoreCerca.current.replaceChildren();
		contenitoreMappa.current.replaceChildren();

		// PRIMO AVVIO della pagina (29 set 2026): si parte SEMPRE da Via Cimino 3, L'Aquila, qualunque valore
		// abbia il modello (testo iniziale, o coordinate ricordate dal browser). Ai rimontaggi successivi del
		// pannello (apertura/chiusura della sezione) si riparte invece dal valore corrente.
		const partenza = !primoAvvioFatto;
		primoAvvioFatto = true;
		// (5 ott) se il modello e' GIA' al punto di partenza (dalla v31 il suo valore iniziale sono le coordinate di
		// Via Cimino 3) non si manda niente: il calcolo iniziale e' quello gia' in cache su ShapeDiver, zero calcoli
		const valoreModello = leggiValore(String(state.uiValue ?? ""));
		const modelloGiaAllaPartenza =
			partenza && !!valoreModello.centro && !valoreModello.segnaposto && distanzaMetri(valoreModello.centro, CENTRO_PREDEFINITO) < 3;
		attesaPartenzaRef.current = partenza && !modelloGiaAllaPartenza;
		if (modelloGiaAllaPartenza) ultimoInviato.current = scriviCoord(valoreModello.centro as [number, number]);
		// diagnostica dell'avvio (per le prove dal browser): window.__eidosMappaAvvio
		const diag = ((window as unknown as {__eidosMappaAvvio?: Record<string, unknown>}).__eidosMappaAvvio ??= {});
		Object.assign(diag, {partenza, modelloGiaAllaPartenza, valoreAlMontaggio: String(state.uiValue ?? "")});
		const iniziale = partenza ? {centro: null, segnaposto: null} : valoreModello;
		const centro = iniziale.centro ?? CENTRO_PREDEFINITO;
		segnapostoRef.current = iniziale.segnaposto ?? centro;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const mappa: any = new mapboxgl.Map({
			container: contenitoreMappa.current,
			style: "mapbox://styles/mapbox/light-v11",
			center: centro,
			zoom: 14.5,
			attributionControl: false,
			dragRotate: false,
			pitchWithRotate: false,
			touchPitch: false,
		});
		mappa.addControl(new mapboxgl.AttributionControl({compact: true}));
		mappa.addControl(new mapboxgl.NavigationControl({showCompass: false}), "top-right");
		mappaRef.current = mappa;
		// per capire i problemi della mappa dalla console del browser (errori di Mapbox e stato della mappa)
		// COLORI DELLA MAPPA (Davide, 29 set 2026): strade in ottanio (come il bordo della maschera), sfondo crema
		// del sito, spessori delle strade invariati. Si cambiano i livelli dello stile "light" appena caricato.
		const colora = () => {
			try {
				const stile = mappa.getStyle();
				for (const l of stile?.layers ?? []) {
					if (l.type === "background") mappa.setPaintProperty(l.id, "background-color", CREMA_MAPPA);
					else if (l.type === "fill" && /^(land|landcover|land-structure)/.test(l.id) && !/park|grass|wood|pitch|cemetery|hospital|school|sand|glacier/.test(l.id))
						mappa.setPaintProperty(l.id, "fill-color", CREMA_MAPPA);
					else if (l.type === "line" && /road|bridge|tunnel|street|path|motorway|primary|secondary|trunk|link|pedestrian/.test(l.id)) {
						// "case"/"shadow" sono i bordi delle strade: color sfondo, cosi' la strada resta pulita
						mappa.setPaintProperty(l.id, "line-color", /case|shadow|oneway/.test(l.id) ? CREMA_MAPPA : OTTANIO);
					}
				}
			} catch (err) {
				console.warn("Mappa Eidos: colori non applicati", err);
			}
		};
		mappa.on("style.load", colora);
		if (mappa.isStyleLoaded && mappa.isStyleLoaded()) colora();
		mappa.on("error", (e: {error?: {message?: string}}) =>
			console.error("Mappa Eidos:", e?.error?.message ?? e),
		);
		(window as unknown as {__eidosMappa?: unknown}).__eidosMappa = mappa;
		// ZOOM CON LE DITA (Davide, 28 set 2026): i pulsanti "Zoom mappa" sono nascosti nel fork. Quando il cliente
		// zooma la mappa (dita, rotella, +/-), si calcola quale dei 4 livelli del modello (8/11/14/18 metri per mm)
		// farebbe stare il contorno in ~2/3 del riquadro con quello zoom, e si imposta QUEL livello: il modello
		// ricalcola (1 calcolo) e la mappa si riallinea al livello scelto. Solo 4 livelli = pochi calcoli.
		mappa.on("zoomend", (e: {originalEvent?: unknown}) => {
			if (!e.originalEvent) return; // zoom nostro (easeTo di adattaZoom): non e' il cliente
			try {
				const {forme: f, chiave: ch, kZoom: kAttuale} = statoZoomRef.current;
				const poly = f?.[ch];
				if (!poly || !poly.length) return;
				const cont = mappa.getContainer() as HTMLElement;
				const lato = Math.min(cont.clientWidth, cont.clientHeight);
				let ext = 0;
				for (const [x, y] of poly) ext = Math.max(ext, Math.abs(x), Math.abs(y));
				const c = mappa.getCenter();
				const dLng = 100 / (111320 * Math.cos((c.lat * Math.PI) / 180));
				const p0 = mappa.project([c.lng, c.lat]);
				const p1 = mappa.project([c.lng + dLng, c.lat]);
				const ppm = Math.abs(p1.x - p0.x) / 100; // pixel per metro con lo zoom scelto dal cliente
				if (!(ppm > 0) || !(ext > 0) || !(lato > 80)) return;
				// metri che devono stare nel contorno (2/3 del riquadro) -> metri per mm -> k
				const metri = (0.66 * lato) / ppm;
				const kVoluto = metri / (2 * ext) / 2;
				let nome = "";
				let kScelto = kAttuale;
				let dist = Infinity;
				for (const [n, k] of Object.entries(ZOOM_K)) {
					const d = Math.abs(Math.log(k / kVoluto));
					if (d < dist) {
						dist = d;
						nome = n;
						kScelto = k;
					}
				}
				if (!nome || kScelto === kAttuale) {
					adattaZoomRef.current(); // stesso livello: la mappa torna al suo zoom
					return;
				}
				const {getParameter} = useShapeDiverStoreParameters.getState();
				const st = getParameter(props.namespace, "Zoom mappa");
				if (!st) return;
				const p = st.getState() as IShapeDiverParameter<string>;
				const scelte = (p.definition as {choices?: string[]}).choices ?? [];
				const i = scelte.indexOf(nome);
				if (i < 0) return;
				// (28 set sera) non si esegue subito: si accoda e si manda insieme al centro della mappa (vedi
				// pianifica): con le dita "zoom" e "trascinamento" finiscono insieme e prima partivano DUE calcoli
				// sovrapposti nella stessa sessione (menu e gioiello potevano restare diversi)
				zoomDaMandare.current = {id: p.definition.id, valore: String(i)};
				pianifica();
			} catch {
				/* ignora */
			}
		});
		// se il contenitore cambia misura (apertura della sezione, finestra ridimensionata) la mappa si adatta
		// (quando il riquadro prende una misura nuova, anche lo zoom si riadatta al contorno: la sezione
		// a fisarmonica si apre con un'animazione e la prima misura e' quasi zero)
		const osservatore = new ResizeObserver(() => {
			try {
				// (24 ott 2026) un'animazione della camera e un cambio di misura non vanno d'accordo in Mapbox (il centro
				// scivola): si ferma prima l'animazione
				if (mappa.isEasing()) mappa.stop();
				mappa.resize();
				const cont = contenitoreMappa.current;
				if (cont && cont.clientWidth > 80 && cont.clientHeight > 80) {
					const m = `${cont.clientWidth}x${cont.clientHeight}`;
					if (m !== misuraRiquadro.current) {
						misuraRiquadro.current = m;
						adattaZoomRef.current();
					}
				}
			} catch {
				/* mappa gia' rimossa */
			}
		});
		osservatore.observe(contenitoreMappa.current);
		setTimeout(() => {
			try {
				mappa.resize();
				adattaZoomRef.current(); // non adattaZoom(): qui sarebbe quella del primo disegno, con forme ancora null
			} catch {
				/* mappa gia' rimossa */
			}
		}, 400);

		// casella con i suggerimenti: sta sopra la mappa, in un contenitore nostro
		const cerca = new MapboxGeocoder({
			accessToken: MAPBOX_PK,
			mapboxgl: mapboxgl as any,
			marker: false,
			// (24 ott 2026) niente volo: la mappa la sposta il pannello con un salto secco (gestore "result"). Sul telefono
			// il volo veniva interrotto al primo fotogramma dal cambio di misura della mappa (righe di testo sotto la mappa
			// che compaiono e spariscono) e la mappa restava sul posto vecchio con il segnaposto "fuori dal gioiello"
			flyTo: false,
			language: "it",
			placeholder: "Via, numero civico e citta'",
			types: "address,poi,place,locality,neighborhood",
		});
		// messaggi della casella in italiano (la libreria li ha solo in inglese)
		try {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const g = cerca as any;
			g._renderNoResults = function () {
				this._renderMessage(
					"<div class='mapbox-gl-geocoder--error mapbox-gl-geocoder--no-results'>Nessun indirizzo trovato: prova con via, numero civico e città</div>",
				);
			};
			g._renderError = function () {
				this._renderMessage("<div class='mapbox-gl-geocoder--error'>Ricerca degli indirizzi non disponibile, riprova tra poco</div>");
			};
		} catch {
			/* ignora */
		}
		contenitoreCerca.current.appendChild(
			cerca.onAdd(mappa) as HTMLElement,
		);
		// all'apertura la casella mostra l'indirizzo di partenza (solo testo: nessuna ricerca, nessun calcolo)
		try {
			// solo se si parte davvero dal punto di partenza: se il cliente aveva cercato un altro indirizzo e
			// riapre la sezione, la casella non deve tornare a "Via Cimino 3" (5 ott)
			// (24 ott) ritorno sulla scheda dopo aver scelto un indirizzo: la casella mostra quell'indirizzo
			const seg0 = segnapostoRef.current as [number, number];
			if (!partenza && ultimaScelta && distanzaMetri(ultimaScelta.segnaposto, seg0) < 3) {
				cerca.setInput(ultimaScelta.testo);
				setTrovato(ultimaScelta.testo);
			} else if (partenza || !iniziale.centro || (!iniziale.segnaposto && distanzaMetri(centro, CENTRO_PREDEFINITO) < 3)) {
				cerca.setInput(INDIRIZZO_PREDEFINITO);
			}
		} catch {
			/* ignora */
		}

		// il segnaposto sulla mappa: sta sull'INDIRIZZO scelto e non si muove quando si trascina la mappa
		const marker = new mapboxgl.Marker({element: elementoSegnaposto()});
		markerRef.current = marker;
		cercaRef.current = cerca;
		if (segnapostoRef.current) marker.setLngLat(segnapostoRef.current).addTo(mappa);

		const invia = () => {
			const c = mappa.getCenter();
			const centroArr: [number, number] = [c.lng, c.lat];
			const seg = segnapostoRef.current;
			// "centro" se il segnaposto e' (quasi) al centro, altrimenti "centro|segnaposto"
			const testo =
				seg && distanzaMetri(centroArr, seg) > 3
					? `${scriviCoord(centroArr)}|${scriviCoord(seg)}`
					: scriviCoord(centroArr);
			if (testo === ultimoInviato.current) return;
			ultimoInviato.current = testo;
			indirizzoDaMandare.current = testo;
			pianifica();
		};
		inviaRef.current = invia;

		// il contorno segue la mappa mentre si muove o si ingrandisce
		mappa.on("move", () => disegnaRef.current());
		mappa.on("load", () => disegnaRef.current());
		// primo avvio: il modello riceve le coordinate del punto di partenza (se le ha gia', nessun calcolo)
		// (5 ott) riserva: se "load" della mappa non arriva (rete, stile non caricato) dopo 8 s si sblocca comunque
		let timerPartenza: number | undefined;
		if (partenza && !modelloGiaAllaPartenza) {
			const sblocca = () => {
				if (!attesaPartenzaRef.current) return;
				attesaPartenzaRef.current = false;
				window.clearTimeout(timerPartenza);
				// (5 ott) al momento del montaggio il valore del modello puo' non essere ancora arrivato: si riguarda
				// ADESSO. Se il modello e' gia' a Via Cimino 3 (dalla v31) non si manda niente: zero calcoli.
				const attuale = leggiValore(String(uiRef.current ?? ""));
				const giaLi = !!attuale.centro && !attuale.segnaposto && distanzaMetri(attuale.centro, CENTRO_PREDEFINITO) < 3;
				Object.assign(diag, {valoreAlLoad: String(uiRef.current ?? ""), giaLiAlLoad: giaLi, quando: Math.round(performance.now())});
				if (giaLi) {
					ultimoInviato.current = scriviCoord(attuale.centro as [number, number]);
					return;
				}
				invia();
			};
			mappa.once("load", sblocca);
			timerPartenza = window.setTimeout(sblocca, 8000);
		}
		// mappa ferma dopo un gesto del cliente -> mando il nuovo centro (il segnaposto resta sull'indirizzo).
		// (24 ott 2026) "moveend" con originalEvent invece di "dragend": dragend arrivava PRIMA dell'inerzia (la mappa
		// scivolava ancora e il modello riceveva il punto dove si era alzato il dito) e il doppio tocco / la rotella
		// spostavano il centro senza dragend. I movimenti fatti dal programma non hanno originalEvent.
		mappa.on("moveend", (e: {originalEvent?: unknown}) => {
			if (e.originalEvent) invia();
		});
		// suggerimento scelto -> segnaposto li' e SALTO SECCO della mappa sul posto (jumpTo non si puo' interrompere),
		// poi il centro parte subito per il modello
		cerca.on(
			"result",
			(ev: {result: {place_name?: string; center?: [number, number]; geometry?: {coordinates?: [number, number]}}}) => {
				const nome = ev.result?.place_name ?? "";
				setTrovato(nome);
				// iPhone: la libreria rimette il fuoco nella casella e la tastiera restava aperta sopra la mappa
				try {
					contenitoreCerca.current?.querySelector<HTMLInputElement>("input")?.blur();
				} catch {
					/* ignora */
				}
				const c = ev.result?.center ?? ev.result?.geometry?.coordinates;
				if (!c) return;
				const punto: [number, number] = [c[0], c[1]];
				segnapostoRef.current = punto;
				marker.setLngLat(punto).addTo(mappa);
				ultimaScelta = {testo: nome, segnaposto: punto};
				mappa.jumpTo({center: punto});
				invia();
				adattaZoomRef.current();
			},
		);
		cerca.on("clear", () => {
			setTrovato("");
			ultimaScelta = null;
		});

		// scheda del browser tornata in primo piano: Chrome ferma i disegni delle schede nascoste e Mapbox
		// puo' restare a meta' caricamento -> un "resize" la fa ripartire
		const suVisibile = () => {
			if (document.visibilityState === "visible") {
				try {
					mappa.resize();
					mappa.triggerRepaint();
				} catch {
					/* mappa gia' rimossa */
				}
			}
		};
		document.addEventListener("visibilitychange", suVisibile);

		const contenitoreCercaEl = contenitoreCerca.current;
		return () => {
			document.removeEventListener("visibilitychange", suVisibile);
			osservatore.disconnect();
			try {
				cerca.onRemove();
			} catch {
				/* gia' rimosso */
			}
			contenitoreCercaEl?.replaceChildren();
			mappa.remove();
			mappaRef.current = null;
			markerRef.current = null;
			cercaRef.current = null;
		};
		// la mappa si crea una volta sola: le modifiche successive arrivano dagli eventi
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// forma, misura o zoom cambiati: contorno nuovo e zoom della mappa adattato
	useEffect(() => {
		disegnaRef.current();
		adattaZoom();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [chiave, kZoom, forme]);

	// (5 ott sera) sul telefono di Davide la casella era VUOTA all'apertura: riserva, dopo 1,5 s se la casella e'
	// ancora vuota e la mappa sta al punto di partenza, ci si scrive l'indirizzo di partenza
	useEffect(() => {
		const t = window.setTimeout(() => {
			try {
				const casella = contenitoreCerca.current?.querySelector<HTMLInputElement>("input");
				const v = leggiValore(String(uiRef.current ?? ""));
				// (24 ott) "alla partenza" solo se anche il segnaposto e' li' (prima bastava il centro)
				const allaPartenza = !v.centro || (!v.segnaposto && distanzaMetri(v.centro, CENTRO_PREDEFINITO) < 3);
				if (casella && !casella.value && allaPartenza) cercaRef.current?.setInput(INDIRIZZO_PREDEFINITO);
			} catch {
				/* ignora */
			}
		}, 1500);
		return () => window.clearTimeout(t);
	}, []);

	// se il valore cambia da fuori (es. ripristino di uno stato salvato), riporto mappa e segnaposto li'
	useEffect(() => {
		// primo avvio: finche' la mappa non ha mandato il punto di partenza, il valore ricordato dal modello
		// (es. le coordinate dell'ultima sessione) NON deve riportare la mappa indietro
		if (attesaPartenzaRef.current) return;
		const v = leggiValore(String(state.uiValue ?? ""));
		const mappa = mappaRef.current;
		if (!v.centro || !mappa) return;
		const seg = v.segnaposto ?? v.centro;
		const testo =
			v.segnaposto && distanzaMetri(v.centro, v.segnaposto) > 3
				? `${scriviCoord(v.centro)}|${scriviCoord(v.segnaposto)}`
				: scriviCoord(v.centro);
		if (testo === ultimoInviato.current) return;
		ultimoInviato.current = testo;
		segnapostoRef.current = seg;
		if (markerRef.current) markerRef.current.setLngLat(seg).addTo(mappa);
		mappa.jumpTo({center: v.centro});
		// ritorno al punto di partenza (totem): la casella di ricerca torna all'indirizzo di partenza
		if (testo === scriviCoord(CENTRO_PREDEFINITO)) {
			try {
				// (5 ott) NIENTE clear(): la libreria rimette a fuoco la casella e sul totem si apriva la tastiera
				// da sola (anche riaprendo la sezione Indirizzo). Basta scrivere il testo e togliere il "trovato".
				setTrovato("");
				ultimaScelta = null;
				cercaRef.current?.setInput(INDIRIZZO_PREDEFINITO);
			} catch {
				/* ignora */
			}
		}
	}, [state.uiValue]);

	return (
		<Box
			style={{
				opacity: disabled ? 0.6 : 1,
				pointerEvents: disabled ? "none" : "auto",
			}}
		>
			<Text size="xs" c="dimmed" mb={4} className="eidos-mappa-aiuto">
				Scrivi l&apos;indirizzo e scegli il suggerimento. Il contorno mostra
				la zona che finisce sul gioiello: trascina la mappa per scegliere la
				zona, tenendo il segnaposto dentro il contorno.
			</Text>
			<div ref={contenitoreCerca} className="eidos-cerca" />
			<div
				className={"eidos-mappa-riquadro" + (controllo && !servizioGiu ? " eidos-mappa-attesa" : "")}
				style={{
					position: "relative",
					height: 260,
					marginTop: 8,
					border: "1px solid #d9d4cd",
				}}
			>
				<div
					ref={contenitoreMappa}
					style={{position: "absolute", inset: 0}}
				/>
				{/* contorno del gioiello: il centro della mappa = il centro del gioiello */}
				<svg
					style={{
						position: "absolute",
						inset: 0,
						width: "100%",
						height: "100%",
						pointerEvents: "none",
					}}
				>
					{/* VELO INVERTITO (Davide, 29 set 2026): la parte che si taglia resta pulita, tutto il resto della
					    mappa e' velato (stesso colore di prima); il bordo ottanio resta sul contorno */}
					<path
						ref={veloRef}
						d=""
						fill="rgba(19,77,93,0.12)"
						fillRule="evenodd"
						stroke="none"
					/>
					<path
						ref={contornoRef}
						d=""
						fill="none"
						stroke={OTTANIO}
						strokeWidth={2}
						strokeLinejoin="round"
					/>
				</svg>
			</div>
			{fuori && (
				<Text size="xs" mt={4} c={ROSSO}>
					Il segnaposto e&apos; fuori dal gioiello: sposta la mappa finche&apos;
					non torna dentro il contorno.
				</Text>
			)}
			{controllo && !servizioGiu && (
				<Text size="xs" mt={4} c="dimmed" className="eidos-strade-controllo">
					Cerco le strade della zona&hellip;
				</Text>
			)}
			{servizioGiu && (
				<div className="eidos-strade-avviso">
					<Text size="xs" c={ROSSO}>
						Il servizio delle mappe (OpenStreetMap) non risponde in questo momento: il gioiello non e&apos;
						stato aggiornato. Riprova tra qualche minuto.
					</Text>
					<Button size="xs" mt={6} onClick={riprova}>
						Riprova
					</Button>
				</div>
			)}
			{trovato && (
				<Text size="xs" mt={4}>
					Trovato: <b>{trovato}</b>
				</Text>
			)}
		</Box>
	);
}

/**
 * Componente per i parametri di tipo testo: il campo "Indirizzo" diventa il pannello mappa,
 * tutti gli altri restano il campo di testo standard di ShapeDiver.
 */
export default function EidosMappaComponent(
	props: PropsParameterComponent & Partial<PropsParameterWrapper>,
) {
	const {definition} = useParameterComponentCommons<string>(props, 0);
	const nome = definition?.displayname || definition?.name || "";
	if (nome === NOME_PARAMETRO_INDIRIZZO) {
		return <PannelloMappa {...props} />;
	}
	return <ParameterStringComponent {...props} />;
}
